﻿import React, {JSX, SyntheticEvent, useCallback} from "react";
import {useControlId} from "../helpers/useControlId";
import FileInput, {FileInputHelp} from "../HtmlControls/FileInput";
import {useMvvmFieldSubscription} from "./MvvmDataProvider";
import {MvvmPrefixedControlProps} from "./MvvmControlProps";

import {useRegisterValidation} from "./useRegisterValidation";
import {unwrapMvvmContext} from "./useMvvmTracking";

export interface MvvmFilesProps<T extends object> extends MvvmPrefixedControlProps<T, FileList | undefined> {
	multiple?: boolean;
	accept?: string;
	capture?: "user" | "environment" | undefined;
	help?: FileInputHelp;
}

export default function MvvmFiles<T extends object = never>(props: MvvmFilesProps<T>): JSX.Element {
	const {
		context: contextProp,
		property,
		id,
		label,
		hint,
		help,
		column,
		controlPrefix,
		controlSuffix,
		hidden,
		disabled,
		inlineError,
		validation,
		onChange,
		onBlur,
		multiple,
		accept,
		capture
	} = props;

	// Controls read the raw context; see unwrapMvvmContext.
	const context = unwrapMvvmContext(contextProp);
	const controlId = useControlId(id);

	const onChangeInternal = useCallback(function (e: SyntheticEvent<HTMLInputElement>): void {
		const value = e.currentTarget.files;
		onChange?.(value as FileList);
		context.setValue(property, value);
	}, [context, property, onChange]);

	useRegisterValidation(context, property, controlId, hidden, validation);
	useMvvmFieldSubscription(context, property);

	return <FileInput label={label}
	                  name={property}
	                  value={context.getValue<FileList | null>(property)}
	                  controlSuffix={controlSuffix}
	                  controlPrefix={controlPrefix}
	                  hint={hint}
	                  help={help}
	                  hidden={hidden}
	                  column={column}
	                  id={id}
	                  required={validation?.required != null}
	                  error={context.getError(property)?.error}
	                  inlineError={inlineError}
	                  disabled={disabled}
	                  onChange={onChangeInternal}
	                  onBlur={onBlur}
	                  multiple={multiple}
	                  accept={accept}
	                  capture={capture}
	/>;
};
